<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue"
import { storeToRefs } from "pinia"
import { useRoute, useRouter } from "vue-router"
import { useAppToast } from "@/shared/lib/useAppToast"

import Button from "primevue/button"
import Card from "primevue/card"
import InputText from "primevue/inputtext"
import Carousel from "primevue/carousel"

import { getCatalogProducts } from "@/modules/catalog/api/catalog.service"
import { getAllCategories } from "@/modules/admin/categories/api/categories.service"

import { useCartStore } from "@/modules/cart/store/cart.store"
import { useAuthStore } from "@/modules/auth/store/auth.store"
import { usePublicCarouselStore } from "@/shared/stores/public-carousel.store"

import AddToCartModal from "@/modules/cart/components/AddToCartModal.vue"
import ProductImage from "@/shared/ui/ProductImage.vue"
import CategoryCircleCard from "@/shared/ui/CategoryCircleCard.vue"
import { usePagination } from "@/shared/composables/usePagination"
import { catalogScrollPolicy } from "@/app/router/scroll"

const route = useRoute()
const router = useRouter()
const cartStore = useCartStore()
const authStore = useAuthStore()
const carouselStore = usePublicCarouselStore()
const { items: carouselItems, status: carouselStatus } = storeToRefs(carouselStore)
const toast = useAppToast()

const products = ref([])
const categories = ref([])
const loading = ref(false)
const cartLoading = ref(false)
const addToCartLoading = ref(false)

const search = ref("")
const selectedCategoryId = ref(null)
const minPrice = ref(null)
const maxPrice = ref(null)

const sidebarOpen = ref(false)

const showAddToCartModal = ref(false)
const selectedProduct = ref(null)
const catalogReady = ref(false)
const unregisterScroll = catalogScrollPolicy.registerCatalog(() => ({
  fullPath: route.fullPath,
  ready: catalogReady.value && !loading.value,
}))
onBeforeUnmount(unregisterScroll)

async function loadProducts() {
  try {
    loading.value = true
    products.value = await getCatalogProducts()
  } catch (error) {
    products.value = []
    toast.error(error.message || "Ошибка при загрузке товаров")
  } finally {
    loading.value = false
  }
}

async function loadCategories() {
  try {
    categories.value = await getAllCategories()
  } catch (error) {
    categories.value = []
  }
}

async function loadCart() {
  if (!authStore.isAuthenticated) return

  try {
    cartLoading.value = true
    await cartStore.loadUserCarts()
  } catch (error) {
    toast.error(error.message || "Ошибка загрузки проектов")
  } finally {
    cartLoading.value = false
  }
}

function syncCategoryFromRoute() {
  const routeCategoryId = Number(route.query.categoryId)

  if (Number.isInteger(routeCategoryId) && routeCategoryId > 0) {
    selectedCategoryId.value = routeCategoryId
  } else {
    selectedCategoryId.value = null
  }
}

const categoryOptions = computed(() => {
  const uniqueCategories = []
  const usedIds = new Set()

  for (const product of products.value) {
    if (product.categoryId && !usedIds.has(product.categoryId)) {
      usedIds.add(product.categoryId)

      uniqueCategories.push({
        label: product.categoryName,
        value: product.categoryId,
      })
    }
  }

  return uniqueCategories.sort((a, b) => a.label.localeCompare(b.label, "ru"))
})

function getCategoryImage(categoryId) {
  const category = categories.value.find(
    (item) => Number(item.id ?? item.Id) === Number(categoryId),
  )

  return category?.imageUrl ?? category?.ImageUrl ?? null
}

function getActualPrice(product) {
  const effectivePrice = Number(product.effectivePriceKzt)

  return Number.isNaN(effectivePrice) ? 0 : effectivePrice
}

function formatPrice(value) {
  const number = Number(value)

  if (Number.isNaN(number)) {
    return "0 ₸"
  }

  return `${number.toLocaleString("ru-RU")} ₸`
}

const filteredProducts = computed(() => {
  return products.value.filter((product) => {
    if (!product.isActive) {
      return false
    }

    const searchText = search.value.trim().toLowerCase()

    const matchesSearch =
      !searchText ||
      (product.name ?? "").toLowerCase().includes(searchText) ||
      String(product.articul ?? "").toLowerCase().includes(searchText) ||
      (product.brandName ?? "").toLowerCase().includes(searchText) ||
      (product.categoryName ?? "").toLowerCase().includes(searchText)

    const matchesCategory =
      !selectedCategoryId.value || product.categoryId === selectedCategoryId.value

    const actualPrice = getActualPrice(product)

    const matchesMinPrice =
      minPrice.value === null ||
      minPrice.value === "" ||
      actualPrice >= Number(minPrice.value)

    const matchesMaxPrice =
      maxPrice.value === null ||
      maxPrice.value === "" ||
      actualPrice <= Number(maxPrice.value)

    return matchesSearch && matchesCategory && matchesMinPrice && matchesMaxPrice
  })
})

const { currentPage, totalPages, paginatedItems: paginatedProducts } = usePagination(
  filteredProducts, 24, [selectedCategoryId, search, minPrice, maxPrice],
)

function changePage(page) {
  currentPage.value = Math.max(1, Math.min(totalPages.value, page))
  catalogScrollPolicy.scrollToProducts(route)
}

const categoryCards = computed(() => {
  const map = new Map()

  for (const product of products.value) {
    if (!product.isActive || !product.categoryId) continue

    if (!map.has(product.categoryId)) {
      map.set(product.categoryId, {
        id: product.categoryId,
        name: product.categoryName || "Без категории",
        products: [],
      })
    }

    map.get(product.categoryId).products.push(product)
  }

  return Array.from(map.values())
    .map((category) => {
      const productImages = category.products
        .map((p) => ({ productId: p.id, url: p.defaultImageUrl, name: p.name }))
        .filter((img) => !!img.url)
        .slice(0, 4)

      const categoryImage = getCategoryImage(category.id)

      return {
        id: category.id,
        name: category.name,
        count: category.products.length,
        imageUrl: categoryImage || productImages[0]?.url || null,
        images: productImages,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name, "ru"))
})

const selectedCategoryName = computed(() => {
  if (!selectedCategoryId.value) return "Все товары"

  const category = categoryOptions.value.find(
    (item) => item.value === selectedCategoryId.value,
  )

  return category?.label ?? "Категория"
})

function openProduct(productId) {
  if (!productId) {
    toast.error("Не удалось открыть товар")
    return
  }

  router.push(`/products/${productId}`)
}

function addToCart(product) {
  selectedProduct.value = product
  showAddToCartModal.value = true
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

async function selectCategory(categoryId) {
  sidebarOpen.value = false

  const destination = {
    path: "/products",
    query: categoryId ? { categoryId } : {},
  }
  if (router.resolve(destination).fullPath === route.fullPath) {
    if (categoryId) await catalogScrollPolicy.scrollToProducts(route)
    return
  }
  await router.push(destination)
}

function clearSearch() {
  search.value = ""
}

function navigateTo(link) {
  if (!link) return
  if (link.startsWith("http")) {
    window.open(link, "_blank")
  } else {
    router.push(link)
  }
}

function isProductOnlyCartItem(item, productId) {
  const itemProductId = Number(item.productId ?? item.ProductId)

  const productVariantId =
    item.productVariantId ??
    item.ProductVariantId ??
    null

  return (
    itemProductId === Number(productId) &&
    productVariantId == null
  )
}

function isInCart(product) {
  for (const cart of cartStore.carts) {
    const inRooms = (cart.rooms || []).some(room =>
      (room.items || []).some(item =>
        isProductOnlyCartItem(item, product.id)
      )
    )

    if (inRooms) return true

    const inPlain = (cart.cartItems || []).some(item =>
      isProductOnlyCartItem(item, product.id)
    )

    if (inPlain) return true
  }

  return false
}

watch(
  () => route.query.categoryId,
  () => {
    syncCategoryFromRoute()
  },
  { flush: 'sync' },
)

async function handleAddToCartConfirm(payload) {
  try {
    addToCartLoading.value = true

    const {
      cartId,
      newCartName,
      roomId,
      createNewCart,
      createNewRoom,
      newRoomName
    } = payload

    let targetCartId = cartId
    let targetRoomId = roomId

    if (createNewCart && newCartName) {
      const createdId = await cartStore.createCart(newCartName)
      targetCartId = createdId
      if (!targetRoomId && targetRoomId !== null) {
        targetRoomId = null
      }
    }

    if (!targetCartId) return

    if (createNewRoom && newRoomName && targetCartId) {
      await cartStore.createRoom({
        cartId: targetCartId,
        name: newRoomName
      })

      await cartStore.selectCart(targetCartId)

      const newRoom = cartStore.enrichedRooms.find(r => r.name === newRoomName)
      targetRoomId = newRoom?.id || null
    }

    await cartStore.addProductToCart({
      productId: selectedProduct.value.id,
      productVariantId: null,
      cartId: targetCartId,
      roomId: targetRoomId,
      quantity: payload.quantity ?? 1
    })

    showAddToCartModal.value = false
    await cartStore.selectCart(targetCartId)
    toast.success("Товар добавлен в проект")
  } catch (error) {
    toast.error(error.message || "Ошибка добавления товара в проект")
  } finally {
    addToCartLoading.value = false
  }
}

onMounted(async () => {
  syncCategoryFromRoute()

  await Promise.all([
    carouselStore.ensureLoaded().catch(() => {}),
    loadCategories(),
    loadProducts(),
  ])
  catalogReady.value = true

  await loadCart()
})

</script>

<template>
  <div class="catalog-shell">
    <div v-if="sidebarOpen" class="sidebar-overlay" aria-hidden="true" @click="sidebarOpen = false"></div>

    <aside id="catalog-categories" class="categories-sidebar" :class="{ open: sidebarOpen }" :aria-hidden="!sidebarOpen">
      <div class="sidebar-header">
        <div>
          <h3 class="sidebar-title">Категории</h3>
        </div>

        <button type="button" class="sidebar-close" aria-label="Закрыть категории" @click="sidebarOpen = false">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <ul class="sidebar-categories">
        <li class="sidebar-category-item" :class="{ active: !selectedCategoryId }" @click="selectCategory(null)">
          <span>Все товары</span>
          <i class="pi pi-arrow-right"></i>
        </li>

        <li v-for="cat in categoryCards" :key="cat.id" class="sidebar-category-item"
          :class="{ active: selectedCategoryId === cat.id }" @click="selectCategory(cat.id)">
          <span>{{ cat.name }}</span>
          <small>{{ cat.count }}</small>
        </li>
      </ul>
    </aside>

    <button type="button" class="floating-menu-btn" :class="{ active: sidebarOpen }" title="Категории"
      aria-label="Категории" aria-controls="catalog-categories" :aria-expanded="sidebarOpen" @click="toggleSidebar">
      <span aria-hidden="true"></span>
      <span aria-hidden="true"></span>
      <span aria-hidden="true"></span>
    </button>

    <section class="catalog-hero">
      <Carousel v-if="carouselStatus === 'success' && carouselItems.length > 0" :value="carouselItems" :numVisible="1" :numScroll="1"
        :autoplayInterval="5000" :circular="true" :showIndicators="false"
        :showNavigators="carouselItems.length > 1"
        class="catalog-carousel">
        <template #item="{ data }">
          <div class="carousel-slide">
            <img :src="data.imageUrl" :alt="data.title ?? ''" class="carousel-image" />

            <div class="carousel-content-wrapper" :class="[
              'position-' + (data.titlePosition || 'center'),
              'align-' + (data.textAlign || 'center')
            ]">
              <div class="carousel-content">
                <span class="hero-kicker">Каталог</span>

                <h1 v-if="data.title?.trim()" class="hero-title" :style="{
                  fontSize: (data.titleSize || 42) + 'px',
                  color: data.titleColor || '#FFFFFF'
                }">
                  {{ data.title }}
                </h1>

                <p v-if="data.subtitle" class="hero-subtitle" :style="{
                  fontSize: (data.subtitleSize || 16) + 'px',
                  color: data.subtitleColor || '#E0E0E0'
                }">
                  {{ data.subtitle }}
                </p>

                <Button v-if="data.link && data.buttonText" :label="data.buttonText"
                  class="hero-btn" @click="navigateTo(data.link)" />
              </div>
            </div>
          </div>
        </template>
      </Carousel>

      <div v-else-if="carouselStatus === 'success' && carouselItems.length === 0" class="catalog-hero-fallback">
        <div class="fallback-content">
          <span>Каталог</span>
          <h1>Мебель для стильных интерьеров</h1>
          <p>
            Дизайнерские предметы для проектов, домов и коммерческих пространств.
          </p>
        </div>
      </div>
    </section>

    <main class="products-page">
      <section class="catalog-search-section">
        <div class="search-panel">
          <i class="pi pi-search"></i>

          <InputText v-model="search" placeholder="Поиск по названию, бренду, артикулу или категории"
            class="catalog-search-input" />

          <button v-if="search" class="clear-search-btn" @click="clearSearch">
            <i class="pi pi-times"></i>
          </button>

          <button class="search-arrow-btn">
            <i class="pi pi-arrow-right"></i>
          </button>
        </div>
      </section>

      <div v-if="loading" class="state-block">
        Загрузка товаров...
      </div>

      <template v-else>
        <section v-if="!selectedCategoryId && !search && categoryCards.length" class="category-cards-section">
          <div class="section-heading">
            <span>Выберите направление</span>
            <h2>Категории товаров</h2>
          </div>

          <div class="category-cards-grid">
            <CategoryCircleCard v-for="cat in categoryCards" :key="cat.id"
              :name="cat.name" :image-url="cat.imageUrl" :count="cat.count"
              @select="selectCategory(cat.id)" />
          </div>
        </section>

        <section v-if="!selectedCategoryId && !search && categoryCards.length" class="categories-overview">
          <section v-for="cat in categoryCards" :key="cat.id" class="category-section">
            <div class="category-section-header">
              <span class="category-line"></span>
              <h3 class="category-section-name">{{ cat.name }}</h3>
              <span class="category-line"></span>
            </div>

            <div class="category-preview-grid">
              <button v-for="(img, idx) in cat.images" :key="idx" type="button" class="category-preview-btn"
                @click="selectCategory(cat.id)">
                <ProductImage :src="img.url" :alt="img.name" />

                <div class="category-preview-overlay">
                  <span>{{ img.name }}</span>
                </div>
              </button>

              <div v-for="n in Math.max(0, 4 - cat.images.length)" :key="'ph-' + n"
                class="category-preview-btn category-preview-placeholder">
                <ProductImage />
              </div>
            </div>

            <div class="category-section-action">
              <Button label="Смотреть категорию" icon="pi pi-arrow-right" iconPos="right" text class="view-all-btn"
                @click="selectCategory(cat.id)" />
            </div>
          </section>
        </section>

        <section v-else id="catalog-products" class="products-section">
          <div class="products-heading">
            <div>
              <span class="products-kicker">
                {{ selectedCategoryId ? "Категория" : "Результаты поиска" }}
              </span>

              <h2>{{ selectedCategoryId ? selectedCategoryName : "Найденные товары" }}</h2>
            </div>

            <Button v-if="selectedCategoryId" label="Все категории" icon="pi pi-arrow-left" class="back-btn" outlined
              @click="selectCategory(null)" />
          </div>

          <div class="category-filter-bar">
            <div class="price-filter">
              <span>Цена</span>

              <InputText v-model="minPrice" type="number" placeholder="от" aria-label="Минимальная цена" class="price-input" />

              <InputText v-model="maxPrice" type="number" placeholder="до" aria-label="Максимальная цена" class="price-input" />

              <button v-if="minPrice || maxPrice" class="reset-price-btn" @click="minPrice = null; maxPrice = null">
                Сбросить
              </button>
            </div>
          </div>

          <div v-if="filteredProducts.length === 0" class="state-block">
            По вашему запросу товары не найдены
          </div>

          <div v-else class="products-grid">
            <Card v-for="product in paginatedProducts" :key="product.id" class="product-card">
              <template #content>
                <div class="product-content">
                  <div class="product-image-wrap product-image-link" role="button" tabindex="0"
                    @click="openProduct(product.id)" @keydown.enter.prevent="openProduct(product.id)"
                    @keydown.space.prevent="openProduct(product.id)">
                    <ProductImage :src="product.defaultImageUrl" :alt="product.name" />

                    <div v-if="product.saleIsActive" class="sale-badge">
                      Скидка
                    </div>
                  </div>

                  <div class="product-info">
                    <div class="product-meta">
                      <span class="product-brand">{{ product.brandName }}</span>
                      <span class="product-category">{{ product.categoryName }}</span>
                    </div>

                    <h3 class="product-name">{{ product.name }}</h3>

                    <div class="price-block">
                      <span
                        v-if="
                          product.saleIsActive &&
                          product.salePrice != null &&
                          product.salePriceKzt != null
                        "
                        class="old-price"
                      >
                        {{ formatPrice(product.basePriceKzt) }}
                      </span>

                      <span class="actual-price">
                        {{ formatPrice(getActualPrice(product)) }}
                      </span>
                    </div>

                    <div class="product-actions">
                      <Button icon="pi pi-eye" class="details-btn" outlined @click="openProduct(product.id)" />

                      <Button :label="isInCart(product) ? 'В проекте' : 'В проект'" icon="pi pi-shopping-cart"
                        class="cart-btn" :loading="addToCartLoading && selectedProduct?.id === product.id"
                        :disabled="addToCartLoading || isInCart(product)" @click="addToCart(product)" />
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
          <nav v-if="totalPages > 1" class="catalog-pagination" aria-label="Страницы товаров">
            <Button icon="pi pi-chevron-left" outlined aria-label="Предыдущая страница"
              :disabled="currentPage === 1" @click="changePage(currentPage - 1)" />
            <span aria-live="polite">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined aria-label="Следующая страница"
              :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)" />
          </nav>
        </section>

        <div v-if="!categoryCards.length && !filteredProducts.length" class="state-block">
          Категории не найдены
        </div>
      </template>
    </main>

    <footer class="footer">
      <div class="footer-content">
        <div class="footer-brand">
          <p>Дизайнерский интерьер в каждой детали</p>
        </div>

        <div class="footer-info">
          <p>Казахстан, Алматы, улица Мирас 2/28, 3й этаж</p>
          <p>+7 700 280 02 82</p>
          <p>info@inredbox.com</p>
        </div>
      </div>
    </footer>

    <AddToCartModal :visible="showAddToCartModal" :carts="cartStore.carts" :product="selectedProduct"
      :loading="addToCartLoading" @close="showAddToCartModal = false" @confirm="handleAddToCartConfirm" />
  </div>
</template>

<style scoped>
.catalog-shell {
  min-height: 100vh;
  background: #ffffff;
  color: #1f2933;
}

/* ─── Sidebar ─── */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 14, 12, 0.46);
  /* The open drawer must sit above the sticky navbar (z-index: 1000). */
  z-index: 1001;
  backdrop-filter: blur(3px);
}

.categories-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 315px;
  max-width: 86vw;
  height: 100vh;
  background: rgba(255, 252, 248, 0.96);
  z-index: 1002;
  transform: translateX(-100%);
  transition: transform 0.32s ease;
  visibility: hidden;
  box-shadow: none;
  border-right: 1px solid rgba(33, 27, 22, 0.08);
  overflow-y: auto;
  backdrop-filter: blur(20px);
}

.categories-sidebar.open {
  transform: translateX(0);
  visibility: visible;
  box-shadow: 18px 0 60px rgba(20, 15, 12, 0.18);
}

.sidebar-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgba(255, 252, 248, 0.94);
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 24px 20px;
  border-bottom: 1px solid rgba(33, 27, 22, 0.08);
}

.sidebar-kicker {
  display: inline-flex;
  color: #c41217;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin-bottom: 8px;
}

.sidebar-title {
  margin: 0;
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.05em;
  color: #1d1713;
}

.sidebar-close {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(33, 27, 22, 0.12);
  background: #fff;
  color: #1d1713;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: 0.2s ease;
}

.sidebar-close:hover {
  background: #c41217;
  color: #fff;
  border-color: #c41217;
}

.sidebar-categories {
  list-style: none;
  margin: 0;
  padding: 14px 0 28px;
}

.sidebar-category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 24px;
  color: #4e4741;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: 0.2s ease;
}

.sidebar-category-item span {
  font-size: 14px;
  line-height: 1.25;
}

.sidebar-category-item small {
  color: #a39a91;
  font-size: 12px;
}

.sidebar-category-item:hover {
  background: rgba(196, 18, 23, 0.055);
  color: #c41217;
}

.sidebar-category-item.active {
  background: rgba(196, 18, 23, 0.09);
  color: #c41217;
  border-left-color: #c41217;
}

/* ─── Floating menu ─── */
.floating-menu-btn {
  position: fixed;
  top: 14px;
  left: 14px;
  z-index: 1001;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0;
  cursor: pointer;
  transition: background 0.2s ease;
}

.floating-menu-btn > span {
  display: block;
  flex: 0 0 2px;
  width: 18px;
  height: 2px;
  border-radius: 1px;
  background: #333;
}

.floating-menu-btn:hover,
.floating-menu-btn.active {
  background: #f5f5f5;
}

.floating-menu-btn.active {
  visibility: hidden;
  pointer-events: none;
}

.floating-menu-btn:focus-visible {
  outline: 2px solid #666;
  outline-offset: -2px;
}

.products-page {
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 28px 72px;
}

/* ─── Hero carousel ─── */
.catalog-hero {
  overflow: hidden;
  background: transparent;
  box-shadow: none;
  margin-bottom: 32px;
}

.carousel-slide {
  position: relative;
  height: 620px;
  overflow: hidden;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-content-wrapper {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  padding-inline: 56px;
}

.carousel-content {
  width: 100%;
  max-width: 620px;
  padding: 0 40px;
  overflow-wrap: anywhere;
}

.position-top {
  align-items: flex-start;
  padding-top: 80px;
}

.position-center {
  align-items: center;
}

.position-bottom {
  align-items: flex-end;
  padding-bottom: 80px;
}

.align-center {
  justify-content: center;
  text-align: center;
}

.align-left {
  justify-content: flex-start;
  text-align: left;
}

.align-right {
  justify-content: flex-end;
  text-align: right;
}

.hero-kicker {
  display: inline-flex;
  margin-bottom: 18px;
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.38em;
  text-transform: uppercase;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.hero-title {
  margin: 0 0 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.35);
}

.hero-subtitle {
  margin: 0 0 24px;
  line-height: 1.5;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

:deep(.hero-btn.p-button) {
  background: rgba(0, 0, 0, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.75) !important;
  color: #fff !important;
  border-radius: 40px !important;
  padding: 10px 28px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
}

:deep(.hero-btn.p-button:hover) {
  background: #fff !important;
  color: #111 !important;
  transform: translateY(-2px);
}

:deep(.catalog-carousel .p-carousel-content-container),
:deep(.catalog-carousel .p-carousel-content) {
  position: relative;
}

:deep(.catalog-carousel .p-carousel-viewport) {
  min-width: 0;
  width: 100%;
}

:deep(.catalog-carousel .p-carousel-prev-button),
:deep(.catalog-carousel .p-carousel-next-button) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 44px;
  height: 44px;
  padding: 0 !important;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85) !important;
  color: #555 !important;
  border: 1px solid #d8d8d8 !important;
  transition: background 0.2s ease, border-color 0.2s ease;
}

:deep(.catalog-carousel .p-carousel-prev-button:hover),
:deep(.catalog-carousel .p-carousel-next-button:hover) {
  background: #fff !important;
  border-color: #999 !important;
}

:deep(.catalog-carousel .p-carousel-prev-button:focus-visible),
:deep(.catalog-carousel .p-carousel-next-button:focus-visible) {
  outline: 2px solid #666;
  outline-offset: 2px;
}

:deep(.catalog-carousel .p-carousel-prev-button svg),
:deep(.catalog-carousel .p-carousel-next-button svg) {
  width: 14px;
  height: 14px;
}

:deep(.catalog-carousel .p-carousel-prev-button) {
  left: 4px;
}

:deep(.catalog-carousel .p-carousel-next-button) {
  right: 4px;
}

.catalog-hero-fallback {
  height: 620px;
  display: grid;
  place-items: center;
  padding: 40px;
  background-image: url("https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=1600&auto=format&fit=crop");
  background-size: cover;
  background-position: center;
  color: #fff;
}

.fallback-content {
  max-width: 680px;
  text-align: center;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.35);
}

.fallback-content span {
  font-size: 12px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
}

.fallback-content h1 {
  margin: 18px 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(42px, 7vw, 82px);
  line-height: 0.95;
  font-weight: 400;
  letter-spacing: -0.07em;
  text-transform: uppercase;
}

.fallback-content p {
  margin: 0 auto;
  max-width: 520px;
  line-height: 1.7;
  color: #fff;
}

/* ─── Search ─── */
.catalog-search-section {
  position: relative;
  z-index: 5;
  max-width: 940px;
  margin: -30px auto 48px;
  padding: 0 18px;
}

.search-panel {
  min-height: 66px;
  display: grid;
  grid-template-columns: 24px 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 0 10px 0 24px;
  background: rgba(255, 252, 248, 0.96);
  border: 1px solid rgba(33, 27, 22, 0.1);
  box-shadow: 0 18px 48px rgba(33, 27, 22, 0.12);
  backdrop-filter: blur(18px);
}

.search-panel>i {
  color: #c41217;
  font-size: 18px;
}

:deep(.catalog-search-input.p-inputtext) {
  width: 100%;
  border: none;
  box-shadow: none;
  background: transparent;
  border-radius: 0;
  color: #1d1713;
  font-size: 15px;
  padding: 0.9rem 0.3rem;
}

:deep(.catalog-search-input.p-inputtext:enabled:focus) {
  box-shadow: none;
  border: none;
}

.clear-search-btn,
.search-arrow-btn {
  width: 44px;
  height: 44px;
  border: none;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: 0.2s ease;
}

.clear-search-btn {
  background: transparent;
  color: #8d847b;
}

.clear-search-btn:hover {
  color: #c41217;
}

.search-arrow-btn {
  background: #c41217;
  color: #fff;
}

.search-arrow-btn:hover {
  background: #9f0f13;
}

/* ─── Section heading ─── */
.section-heading {
  text-align: center;
  margin-bottom: 26px;
}

.section-heading span {
  color: #c41217;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 10px 0 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(34px, 4.8vw, 62px);
  line-height: 1;
  font-weight: 400;
  letter-spacing: -0.065em;
  text-transform: uppercase;
}

/* ─── Main category cards ─── */
.category-cards-section {
  margin-bottom: 78px;
}

.category-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  justify-items: center;
  gap: 24px;
}

/* ─── States ─── */
.state-block {
  padding: 58px 34px;
  text-align: center;
  border: 1px dashed rgba(33, 27, 22, 0.18);
  color: #7c7168;
  background: rgba(255, 255, 255, 0.65);
  font-size: 15px;
}

/* ─── Category overview ─── */
.categories-overview {
  display: flex;
  flex-direction: column;
  gap: 86px;
}

.category-section {
  width: 100%;
}

.category-section-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
}

.category-line {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(33, 27, 22, 0.22), transparent);
}

.category-section-name {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(30px, 4vw, 52px);
  font-weight: 400;
  letter-spacing: -0.06em;
  text-align: center;
  text-transform: uppercase;
  color: #191411;
}

.category-preview-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.category-preview-btn {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: none;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.category-preview-btn:nth-child(2) {
  border-radius: 32px 0 0 0;
}

.category-preview-btn:nth-child(3) {
  border-radius: 0 0 32px 0;
}

.category-preview-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 22px 50px rgba(33, 27, 22, 0.16);
}

.category-preview-overlay {
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  text-align: left;
}

.category-preview-overlay span {
  display: block;
  font-size: 13px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-preview-placeholder {
  display: grid;
  place-items: center;
  color: #b8afa5;
  font-size: 30px;
}

.category-section-action {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

:deep(.view-all-btn.p-button) {
  color: #171412;
  font-weight: 600;
  border-radius: 0;
  background: transparent;
}

:deep(.view-all-btn.p-button:hover) {
  color: #c41217;
  background: transparent;
}

:deep(.view-all-btn.p-button .p-button-icon) {
  color: #c41217;
}

/* ─── Products ─── */
.products-section {
  margin-top: 18px;
}

.catalog-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.products-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}

.products-kicker {
  display: block;
  margin-bottom: 8px;
  color: #c41217;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.16em;
}

.products-heading h2 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(36px, 5vw, 64px);
  font-weight: 400;
  letter-spacing: -0.07em;
  text-transform: uppercase;
}

:deep(.back-btn.p-button) {
  min-height: 44px;
  padding: 0.45rem 0.75rem !important;
  border-radius: 8px !important;
  font-size: 13px;
  font-weight: 500 !important;
  color: #555 !important;
  border-color: transparent !important;
  background: transparent !important;
}

:deep(.back-btn.p-button:hover),
:deep(.back-btn.p-button:active) {
  color: #111 !important;
  background: #f5f5f5 !important;
}

:deep(.back-btn.p-button:focus-visible),
.reset-price-btn:focus-visible {
  outline: 2px solid #555;
  outline-offset: 3px;
}

.category-filter-bar {
  display: flex;
  justify-content: flex-end;
  margin: -6px 0 24px;
}

.price-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.price-filter span {
  color: #777;
  font-size: 12px;
}

:deep(.price-input.p-inputtext) {
  width: 110px;
  height: 44px;
  border-radius: 8px;
  background: #fafafa;
  border-color: #e5e5e5;
  font-size: 13px;
}

:deep(.price-input.p-inputtext:enabled:focus) {
  border-color: #777;
  box-shadow: 0 0 0 1px #777;
}

.reset-price-btn {
  min-height: 44px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: #666;
  border-radius: 8px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  font-size: 12px;
  transition: 0.2s ease;
}

.reset-price-btn:hover {
  background: #f5f5f5;
  color: #111;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  grid-auto-rows: 1fr;
}

:deep(.product-card.p-card) {
  border-radius: 0 !important;
  overflow: hidden;
  border: 1px solid rgba(33, 27, 22, 0.08);
  box-shadow: none;
  background: rgba(255, 255, 255, 0.82);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

:deep(.product-card.p-card:hover) {
  transform: translateY(-4px);
  border-color: rgba(196, 18, 23, 0.24);
  box-shadow: 0 22px 48px rgba(33, 27, 22, 0.12);
}

:deep(.product-card.p-card .p-card-body),
:deep(.product-card.p-card .p-card-content) {
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.product-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}

.product-image-wrap {
  position: relative;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  padding: 14px 14px 15px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #9a9189;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.product-name {
  flex: 1;
  margin: 0;
  min-height: 40px;
  color: #171412;
  font-size: 15px;
  line-height: 1.35;
  font-weight: 600;
}

.price-block {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.old-price {
  text-decoration: line-through;
  color: #aaa29a;
  font-size: 12px;
}

.actual-price {
  color: #171412;
  font-size: 16px;
  font-weight: 700;
}

.product-actions {
  margin-top: auto;
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 8px;
  padding-top: 6px;
}

:deep(.details-btn.p-button) {
  width: 42px;
  height: 38px;
  border-radius: 0 !important;
  color: #171412;
  border-color: rgba(33, 27, 22, 0.14);
  background: #fff;
}

:deep(.details-btn.p-button:hover) {
  color: #c41217 !important;
  border-color: #c41217 !important;
  background: rgba(196, 18, 23, 0.05) !important;
}

:deep(.cart-btn.p-button) {
  height: 38px;
  border-radius: 0 !important;
  background: #c41217;
  border-color: #c41217;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

:deep(.cart-btn.p-button:hover) {
  background: #9f0f13;
  border-color: #9f0f13;
}

:deep(.cart-btn.p-button:disabled) {
  background: #e5e0da !important;
  border-color: #e5e0da !important;
  color: #7c7168 !important;
  opacity: 1 !important;
}

:deep(.details-btn.p-button) {
  border-radius: 0 !important;
  flex-shrink: 0;
  color: #171412;
  border-color: rgba(33, 27, 22, 0.18);
}

:deep(.cart-btn.p-button) {
  border-radius: 0 !important;
  flex: 1;
  background: #c41217;
  border-color: #c41217;
  color: #fff;
  font-size: 13px;
}

:deep(.cart-btn.p-button:hover) {
  background: #9f0f13;
  border-color: #9f0f13;
}

:deep(.cart-btn.p-button:disabled) {
  background: #e5e0da !important;
  border-color: #e5e0da !important;
  color: #7c7168 !important;
  opacity: 1 !important;
  cursor: not-allowed;
}

:deep(.cart-btn.p-button:disabled .p-button-icon) {
  color: #7c7168 !important;
}

/* ─── Footer ─── */
.footer {
  margin-top: 24px;
  background: linear-gradient(135deg, #171412 0%, #241a17 58%, #c41217 180%);
  color: #ffffff;
  padding: 42px 28px;
}

.footer-content {
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 28px;
}

.footer-logo {
  display: inline-flex;
  padding: 9px 13px;
  border: 1px solid #c41217;
  color: #fff;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.footer-brand p,
.footer-info p {
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
}

.footer-info {
  text-align: right;
}

/* ─── Responsive ─── */
@media (max-width: 1280px) {
  .products-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .products-page {
    padding: 22px 18px 56px;
  }

  .carousel-slide,
  .catalog-hero-fallback {
    height: 520px;
  }

  .category-preview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .products-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .catalog-search-section {
    margin: -24px auto 38px;
    padding: 0 8px;
  }

  .search-panel {
    min-height: 58px;
    grid-template-columns: 20px 1fr auto;
    padding-left: 18px;
  }

  .search-arrow-btn {
    display: none;
  }

  .category-cards-grid {
    gap: 14px;
  }

  .category-section-header {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .category-line {
    display: none;
  }

  .category-preview-grid {
    gap: 12px;
  }

  .products-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .footer-content {
    flex-direction: column;
  }

  .footer-info {
    text-align: left;
  }

  .category-filter-bar {
    justify-content: stretch;
  }

  .price-filter {
    width: 100%;
    flex-wrap: wrap;
  }

  :deep(.price-input.p-inputtext) {
    flex: 1;
    min-width: 120px;
  }
}

@media (max-width: 540px) {
  .products-page {
    padding: 16px 12px 44px;
  }

  .hero-kicker {
    letter-spacing: 0.24em;
  }

  .category-preview-grid,
  .products-grid {
    grid-template-columns: 1fr;
  }

  .search-panel {
    gap: 8px;
  }

  :deep(.catalog-search-input.p-inputtext) {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .carousel-slide,
  .catalog-hero-fallback { height: 420px; }
  .carousel-content { padding: 0; }
  .hero-title { font-size: 28px !important; }
  .hero-subtitle { font-size: 14px !important; }
  .position-top { padding-top: 50px; }
  .position-bottom { padding-bottom: 50px; }
}

@media (max-width: 480px) {
  .carousel-slide,
  .catalog-hero-fallback { height: 340px; }
  .hero-title { font-size: 24px !important; }
  .hero-subtitle { font-size: 12px !important; }
  .hero-kicker { margin-bottom: 12px; }
  :deep(.hero-btn.p-button) { padding: 6px 20px !important; font-size: 12px !important; }
  .catalog-hero-fallback { padding: 20px; }
  .fallback-content h1 { font-size: 32px; }
  .fallback-content p { font-size: 13px; }
}
</style>
